import { useDispatch } from 'react-redux';

import { Button } from '@/components/Buttons';
import { CheckoutHandlerProvider, CheckoutInterception } from '@/components/products/CheckoutHandlerContext';
import renderProductCard from '@/components/products/renderProductCard';
import { useSelector } from '@/redux/helper';

import { chooseProductCopy } from '../copy';
import speedyFlowSlice from '../redux/slice';

const { advanceSpeedyFlow } = speedyFlowSlice.actions;

interface ChooseProductStepProps {
  /**
   * How to intervene when a card is clicked. A signed-out visitor only records their choice — there
   * is no account to bill yet — while a signed-in one goes straight to a checkout tab.
   */
  interception: CheckoutInterception;
}

/**
 * The upsell. Renders the same product cards as the products prompt modal, via the shared
 * renderer, so an offer never differs between the two places it can appear.
 *
 * The only difference is the handoff: the cards would normally navigate the page to Stripe, which
 * would throw away the flow. The provider redirects that into a popup instead.
 */
const ChooseProductStep = ({ interception }: ChooseProductStepProps) => {
  const dispatch = useDispatch();
  const { habitatId, primaryProductType, secondaryProductType, forEducator, gate, machine } = useSelector(
    (state) => state.modals.speedyFlow,
  );

  // The flow is still `guest-purchase` after they log in — it names how they arrived, not where
  // they are. Only the account answers whether there is still an account to fall back to.
  const isSignedOut = !machine?.hasAccount;

  return (
    <CheckoutHandlerProvider value={interception}>
      <div
        className="flex flex-col items-center justify-center gap-4 px-4 py-6 desktop:px-6"
        data-component="speedy-step"
        data-step="choose-product"
      >
        {primaryProductType && renderProductCard(primaryProductType, habitatId, gate?.entryPoint, 'primary', forEducator)}
        {secondaryProductType &&
          renderProductCard(secondaryProductType, habitatId, gate?.entryPoint, 'secondary', forEducator)}

        {/* Only a signed-out visitor has an account to fall back to. */}
        {isSignedOut && (
          <Button variant="link" size="lg" onClick={() => dispatch(advanceSpeedyFlow({ type: 'choseLogIn' }))}>
            {chooseProductCopy.logIn}
          </Button>
        )}
      </div>
    </CheckoutHandlerProvider>
  );
};

export default ChooseProductStep;
